import { useNavigate } from 'react-router-dom';

import { submitWizardOrchestrator } from 'api/Wizard/Wizard';

import { ia } from 'lib/helpers/utility';
import { useCustomMutation } from 'lib/hooks/useCustomMutation';

import { showAlert } from 'components/shared/Alert/Alert';

import { WIZARD_PROGRESS } from './enum';

export default function useOrchestratorSubmission({
  orchestratorData,
  onNext,
  advanceOnStatuses = [WIZARD_PROGRESS.COMPLETED, WIZARD_PROGRESS.IN_PROGRESS],
  invalidateQueryKey,
  invalidateOnSuccess = true
} = {}) {
  const navigate = useNavigate();
  const { practiceId } = orchestratorData || {};

  const normalizedInvalidateQueryKey = ia(invalidateQueryKey)
    ? invalidateQueryKey
    : invalidateQueryKey != null
      ? [invalidateQueryKey]
      : [];

  const scopedInvalidateKeys = [
    practiceId != null && ['wizardInstance', practiceId],
    practiceId != null && ['practice', practiceId],
    practiceId != null ? ['wizard-overview', practiceId] : 'wizard-overview',
    'practices'
  ].filter(Boolean);

  return useCustomMutation({
    mutationFn: async (values = {}) => {
      const handlerFields =
        values && typeof values === 'object' && !Array.isArray(values) ? values : {};
      return submitWizardOrchestrator(navigate, {
        ...orchestratorData,
        handlerFields
      });
    },
    invalidateQueryKey: invalidateOnSuccess
      ? [...normalizedInvalidateQueryKey, ...scopedInvalidateKeys]
      : undefined,
    onSuccess: async (orchestratorResponse) => {
      const rawHandlerStatus = orchestratorResponse?.data?.handlerStatus;
      const status =
        typeof rawHandlerStatus === 'string' ? rawHandlerStatus : rawHandlerStatus?.status;
      const shouldAdvance = advanceOnStatuses.includes(status);
      const responseError = orchestratorResponse?.data?.error || orchestratorResponse?.error;
      const note =
        typeof rawHandlerStatus === 'string' ? responseError || null : rawHandlerStatus?.note;
      let noteObj = {};
      try {
        noteObj = note ? JSON.parse(note) : {};
      } catch (_) {
        noteObj = {};
      }
      if (status === WIZARD_PROGRESS.COMPLETED) {
        if (shouldAdvance) {
          onNext?.();
        }
      } else if (status === WIZARD_PROGRESS.IN_PROGRESS) {
        if (shouldAdvance) {
          onNext?.();
        }
      } else if (status === WIZARD_PROGRESS.FAILED) {
        showAlert({
          color: 'danger',
          message:
            noteObj?.error ||
            note ||
            responseError ||
            'An error occurred while saving practice details.'
        });
      } else if (responseError) {
        showAlert({
          color: 'danger',
          message: responseError
        });
      } else {
        showAlert({
          color: 'warning',
          message:
            noteObj?.error ||
            note ||
            'Practice details saved but not completed. Please check the form.'
        });
      }
    },
    onError: (error) => {
      showAlert({
        color: 'error',
        message: error?.message || 'An error occurred while submitting the form.'
      });
    },
    disableAlert: true
  });
}
